import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import type { UnreconciledOfflineItem } from '@/types'

/**
 * The manager worklist for food that left the kitchen unbilled (F-CTRL-N35).
 *
 * This screen is load-bearing, not informational: an open item HARD-BLOCKS Day
 * Close, and this is the only way to clear one. Without it a single failed
 * offline order would leave a restaurant unable to close its day at all.
 */

/** Turn a stable failure code into something a manager can act on. */
function explainFailure(code: string, orderNumber: string): string {
  const ref = orderNumber ? `#${orderNumber}` : 'The order'
  switch (code) {
    case 'order_not_editable':
      return `${ref} was already paid or voided on another terminal while this one was offline.`
    case 'order_not_found':
      return `${ref} no longer exists — it was removed while this terminal was offline.`
    case 'table_has_active_order':
      return `The table was seated by another terminal while this one was offline, so ${ref.toLowerCase()} could not be opened.`
    case 'offline_order_duplicate':
      return `${ref} was already synced by another attempt.`
    default:
      return `${ref} could not be posted (${code}).`
  }
}

function ItemRow({ item, onResolved }: { item: UnreconciledOfflineItem; onResolved: () => void }) {
  const { toast } = useToast()
  const [note, setNote] = useState('')

  const resolve = useMutation({
    mutationFn: (action: 'billed' | 'written_off') =>
      apiClient.resolveUnreconciledOffline(item.id, action, note.trim()),
    onSuccess: (res) => {
      if (!res.success) {
        toast({ title: 'Could not resolve', description: res.message, variant: 'destructive' })
        return
      }
      toast({ title: 'Resolved', description: 'The item no longer blocks Day Close.' })
      onResolved()
    },
    onError: (e: unknown) => {
      toast({
        title: 'Could not resolve',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      })
    },
  })

  const lineCount = Array.isArray(item.lines) ? item.lines.length : 0
  const noteMissing = note.trim() === ''

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-4 dark:border-amber-800 dark:bg-amber-950/30">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <div className="font-medium">
              {item.order_number ? `Order #${item.order_number}` : 'Offline order'} —{' '}
              {lineCount} item{lineCount === 1 ? '' : 's'} sent to the kitchen
            </div>
            <div className="text-sm text-muted-foreground">
              {explainFailure(item.failure_code, item.order_number)}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Taken {new Date(item.captured_at).toLocaleString()}
            </div>
          </div>

          {lineCount > 0 && (
            <ul className="rounded border bg-background/60 p-2 text-sm">
              {item.lines.map((l, i) => (
                <li key={i} className="tabular-nums">
                  {l.quantity} × <span className="font-mono text-xs">{l.productId}</span>
                  {l.special_instructions ? (
                    <span className="text-muted-foreground"> — {l.special_instructions}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          <Input
            placeholder="Why? e.g. billed to order #20260805-052, or comped — kitchen error"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={noteMissing || resolve.isPending}
              onClick={() => resolve.mutate('billed')}
            >
              {resolve.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Billed to another order
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={noteMissing || resolve.isPending}
              onClick={() => resolve.mutate('written_off')}
            >
              Write off
            </Button>
            {noteMissing && (
              <span className="self-center text-xs text-muted-foreground">
                A reason is required — it is the only record of what happened to this food.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function UnreconciledOfflineItems() {
  const queryClient = useQueryClient()
  const { data, isPending } = useQuery({
    queryKey: ['offline', 'unreconciled'],
    queryFn: () => apiClient.listUnreconciledOffline(),
    refetchInterval: 60_000,
  })

  const items = data?.data?.items ?? []
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['offline', 'unreconciled'] })

  // Stay out of the way when there is nothing to do — this is an exception
  // queue, not a dashboard tile.
  if (isPending || items.length === 0) return null

  return (
    <Card className="border-amber-300 dark:border-amber-800">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          Fired but unbilled ({items.length})
        </CardTitle>
        <CardDescription>
          These items were sent to the kitchen from a terminal that was offline, and
          could not be billed when it reconnected. The food was made. Decide what
          happened to it — <strong>Day Close is blocked until you do</strong>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.map((it) => (
          <ItemRow key={it.id} item={it} onResolved={invalidate} />
        ))}
      </CardContent>
    </Card>
  )
}
